import React from 'react';
import { CircularProgress } from '@mui/material';
import styles from './DataTable.module.scss';

export type DataTableAlign = 'left' | 'center' | 'right';

export interface DataTableColumn<T> {
	id: string;
	header: React.ReactNode;
	accessor?: keyof T | ((row: T) => React.ReactNode);
	render?: (row: T, index: number) => React.ReactNode;
	width?: string | number;
	minWidth?: string | number;
	align?: DataTableAlign;
	className?: string;
	headerClassName?: string;
}

export interface DataTableAction<T> {
	key: string;
	label: string;
	onClick: (row: T, event: React.MouseEvent<HTMLButtonElement>) => void;
	hidden?: (row: T) => boolean;
	disabled?: (row: T) => boolean;
	variant?: 'default' | 'danger';
	icon?: React.ReactNode;
}

export interface DataTableProps<T> {
	columns: DataTableColumn<T>[];
	data: T[];
	getRowKey: (row: T, index: number) => string | number;
	onRowClick?: (row: T, index: number) => void;
	actions?: DataTableAction<T>[];
	actionsHeader?: React.ReactNode;
	actionsWidth?: string | number;
	striped?: boolean;
	loading?: boolean;
	emptyMessage?: React.ReactNode;
	className?: string;
	stickyHeader?: boolean;
}

const toCssSize = (value?: string | number): string | undefined => {
	if (value === undefined) return undefined;
	return typeof value === 'number' ? `${value}px` : value;
};

const getAlignClass = (align?: DataTableAlign): string => {
	if (align === 'center') return styles.alignCenter;
	if (align === 'right') return styles.alignRight;
	return styles.alignLeft;
};

const getCellValue = <T,>(column: DataTableColumn<T>, row: T, index: number): React.ReactNode => {
	if (column.render) {
		return column.render(row, index);
	}

	if (typeof column.accessor === 'function') {
		return column.accessor(row);
	}

	if (column.accessor) {
		return row[column.accessor] as React.ReactNode;
	}

	return null;
};

const DataTable = <T,>({
	columns,
	data,
	getRowKey,
	onRowClick,
	actions,
	actionsHeader = 'Actions',
	actionsWidth = 80,
	striped = true,
	loading = false,
	emptyMessage = 'No data',
	className,
	stickyHeader = true,
}: DataTableProps<T>): JSX.Element => {
	const showActions =
		Boolean(actions?.length) &&
		data.some((row) => (actions || []).some((action) => !action.hidden?.(row)));
	const columnCount = columns.length + (showActions ? 1 : 0);

	const getColumnStyle = (column: DataTableColumn<T>): React.CSSProperties => ({
		width: toCssSize(column.width),
		minWidth: toCssSize(column.minWidth),
	});

	return (
		<div className={`${styles.wrap}${className ? ` ${className}` : ''}`}>
			<div className={styles.scroll}>
				{loading ? (
					<div className={styles.loading}>
						<CircularProgress size={20} color="inherit" />
					</div>
				) : (
					<table className={styles.table}>
						<thead>
							<tr>
								{columns.map((column) => (
									<th
										key={column.id}
										className={`${styles.headCell} ${getAlignClass(column.align)}${
											stickyHeader ? ` ${styles.stickyHead}` : ''
										}${column.headerClassName ? ` ${column.headerClassName}` : ''}`}
										style={getColumnStyle(column)}
										scope="col"
									>
										{column.header}
									</th>
								))}
								{showActions && (
									<th
										className={`${styles.headCell} ${styles.alignRight} ${styles.actionsCell}${
											stickyHeader ? ` ${styles.stickyHead}` : ''
										}`}
										style={{ width: toCssSize(actionsWidth) }}
										scope="col"
									>
										{actionsHeader}
									</th>
								)}
							</tr>
						</thead>
						<tbody>
							{data.length === 0 ? (
								<tr>
									<td className={styles.empty} colSpan={columnCount}>
										{emptyMessage}
									</td>
								</tr>
							) : (
								data.map((row, index) => {
									const visibleActions = (actions || []).filter((action) => !action.hidden?.(row));
									const isClickable = Boolean(onRowClick);
									const rowClass = [
										styles.row,
										striped && index % 2 === 1 ? styles.striped : '',
										isClickable ? styles.clickable : '',
									]
										.filter(Boolean)
										.join(' ');

									return (
										<tr
											key={getRowKey(row, index)}
											className={rowClass}
											onClick={isClickable ? () => onRowClick?.(row, index) : undefined}
											onKeyDown={
												isClickable
													? (event) => {
															if (event.key === 'Enter' || event.key === ' ') {
																event.preventDefault();
																onRowClick?.(row, index);
															}
													  }
													: undefined
											}
											tabIndex={isClickable ? 0 : undefined}
										>
											{columns.map((column) => {
												const value = getCellValue(column, row, index);
												const title = typeof value === 'string' || typeof value === 'number' ? String(value) : undefined;

												return (
													<td
														key={column.id}
														className={`${styles.cell} ${getAlignClass(column.align)}${
															column.className ? ` ${column.className}` : ''
														}`}
														style={getColumnStyle(column)}
														title={title}
													>
														{value}
													</td>
												);
											})}
											{showActions && (
												<td
													className={`${styles.cell} ${styles.alignRight} ${styles.actionsCell}`}
													style={{ width: toCssSize(actionsWidth) }}
												>
													<div className={styles.actions}>
														{visibleActions.map((action) => (
															<button
																key={action.key}
																type="button"
																aria-label={action.label}
																title={action.label}
																className={`${styles.actionButton}${
																	action.icon ? ` ${styles.actionIconButton}` : ''
																}${action.variant === 'danger' ? ` ${styles.actionDanger}` : ''}`}
																disabled={action.disabled?.(row)}
																onClick={(event) => {
																	event.stopPropagation();
																	action.onClick(row, event);
																}}
															>
																{action.icon || action.label}
															</button>
														))}
													</div>
												</td>
											)}
										</tr>
									);
								})
							)}
						</tbody>
					</table>
				)}
			</div>
		</div>
	);
};

export default DataTable;
